import { useState, useEffect, useRef } from 'react';
import { X, Copy, Check, Clock, Loader, CheckCircle2 } from 'lucide-react';
import QRCode from 'qrcode';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const PaymentLinkModal = ({ paymentData, onClose }) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes = 180 seconds
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, success, expired
  const pollingIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // Generate QR Code
  useEffect(() => {
    if (paymentData?.payment_url) {
      QRCode.toDataURL(paymentData.payment_url, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      })
        .then((url) => setQrCodeDataUrl(url))
        .catch((err) => console.error('QR Code generation error:', err));
    }
  }, [paymentData]);

  // Timer countdown
  useEffect(() => {
    if (paymentStatus !== 'pending') return;

    timerIntervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setPaymentStatus('expired');
          clearInterval(timerIntervalRef.current);
          clearInterval(pollingIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [paymentStatus]);

  // Poll payment status every 5 seconds
  useEffect(() => {
    if (!paymentData?.transaction_id || paymentStatus !== 'pending') return;

    const checkPaymentStatus = async () => {
      try {
        const response = await apiService.getPayinStatus(paymentData.transaction_id);

        if (response.success && response.data) {
          const status = response.data.status;

          if (status === 'success' || status === 'completed') {
            setPaymentStatus('success');
            clearInterval(pollingIntervalRef.current);
            clearInterval(timerIntervalRef.current);
            toast.success('Payment received successfully!');
          } else if (status === 'failed' || status === 'cancelled') {
            setPaymentStatus('expired');
            clearInterval(pollingIntervalRef.current);
            clearInterval(timerIntervalRef.current);
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    // Initial check
    checkPaymentStatus();

    // Set up polling
    pollingIntervalRef.current = setInterval(checkPaymentStatus, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [paymentData, paymentStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, []);

  const handleCopyLink = () => {
    if (paymentData?.payment_url) {
      navigator.clipboard.writeText(paymentData.payment_url);
      setCopied(true);
      toast.success('Payment link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    // Clean up intervals
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    onClose();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-y-auto" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {paymentStatus === 'success' ? 'Payment Successful!' :
             paymentStatus === 'expired' ? 'Payment Expired' :
             'Waiting for Payment'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {paymentStatus === 'success' ? (
            // Success State
            <div className="text-center">
              <div className="mx-auto mb-4 relative">
                <div className="h-24 w-24 mx-auto bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
                  <CheckCircle2 className="h-12 w-12 text-green-600 animate-check" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-32 w-32 bg-green-200 rounded-full opacity-20 animate-ping"></div>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Received!
              </h3>
              <p className="text-gray-600 mb-6">
                Transaction completed successfully
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Transaction ID</label>
                    <p className="text-sm text-gray-900 truncate">{paymentData.transaction_id}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Amount</label>
                    <p className="text-sm text-gray-900 font-semibold">{formatCurrency(paymentData.amount)}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="w-full btn-primary"
              >
                Close
              </button>
            </div>
          ) : paymentStatus === 'expired' ? (
            // Expired State
            <div className="text-center">
              <div className="mx-auto h-24 w-24 mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <Clock className="h-12 w-12 text-red-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Payment Link Expired
              </h3>
              <p className="text-gray-600 mb-6">
                The payment link has expired. Please create a new payment link.
              </p>

              <button
                onClick={handleClose}
                className="w-full btn-primary"
              >
                Close
              </button>
            </div>
          ) : (
            // Pending State - Show QR and Link
            <>
              {/* Amount Display */}
              <div className="text-center mb-6">
                <p className="text-sm text-gray-500 mb-1">Amount to Pay</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(paymentData.amount)}
                </p>
              </div>

              {/* Timer */}
              <div className="flex items-center justify-center space-x-2 mb-6">
                <Clock className="h-5 w-5 text-primary-600" />
                <span className={`text-lg font-semibold ${timeLeft < 60 ? 'text-red-600' : 'text-primary-600'}`}>
                  {formatTime(timeLeft)}
                </span>
              </div>

              {/* QR Code */}
              {qrCodeDataUrl && (
                <div className="mb-6">
                  <div className="bg-white border-4 border-gray-200 rounded-lg p-4 mx-auto w-fit">
                    <img
                      src={qrCodeDataUrl}
                      alt="Payment QR Code"
                      className="w-64 h-64 mx-auto"
                    />
                  </div>
                  <p className="text-center text-sm text-gray-600 mt-3">
                    Scan QR code with any UPI app to pay
                  </p>
                </div>
              )}

              {/* Payment Link */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or use payment link
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={paymentData?.payment_url || ''}
                    readOnly
                    className="flex-1 text-sm bg-gray-50 border border-gray-300 rounded px-3 py-2 truncate"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="btn-primary inline-flex items-center px-4 py-2"
                  >
                    {copied ? (
                      <Check size={16} />
                    ) : (
                      <Copy size={16} />
                    )}
                  </button>
                </div>
              </div>

              {/* Status */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Loader className="h-5 w-5 text-blue-600 animate-spin" />
                  <span className="text-sm font-medium text-blue-900">
                    Waiting for payment confirmation...
                  </span>
                </div>
                <p className="text-xs text-blue-700">
                  This page will update automatically when payment is received
                </p>
              </div>

              {/* Transaction Details */}
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs text-gray-500 space-y-1">
                  <div className="flex justify-between">
                    <span>Transaction ID:</span>
                    <span className="font-mono">{paymentData.transaction_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Order ID:</span>
                    <span className="font-mono">{paymentData.order_id}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes scale-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes check {
          0% {
            stroke-dashoffset: 100;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }

        .animate-scale-in {
          animation: scale-in 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }

        .animate-check {
          stroke-dasharray: 100;
          animation: check 0.5s 0.3s cubic-bezier(0.65, 0, 0.45, 1) forwards;
        }
      `}</style>
    </div>
  );
};

export default PaymentLinkModal;
